import React from "react";
import { FiAlertTriangle } from "react-icons/fi";

// Accuracy = 100 - mean absolute error (Leave-One-Woreda-Out)
export const ACCURACY_NOTE = "Calculated from the mean absolute error (Leave-One-Woreda-Out)";
export const accuracyValue = (perf) => (perf?.lowo ? 100 - perf.lowo.mae_pct_points : null);

// Leave-One-Woreda-Out performance of the baseline model on the training
// data sites (feedback_model.py `evaluate` -> "lowo"), each number with what
// it means and how it was measured. `perf` = schisto_risk_model_baseline_accuracy.json fields.
export default function ModelPerformance({ perf, outdated }) {
  const lowo = perf?.lowo;
  if (!lowo) return <p className="rp-muted mp-empty">Leave-One-Woreda-Out not measured yet.</p>;
  return (
    <div className="mp">
      <div className="mp-metrics">
        <div className="mp-metric">
          <span className="mp-metric__value">{lowo.mae_pct_points.toFixed(1)} pts</span>
          <span className="mp-metric__label">Mean absolute error (MAE) · LOWO</span>
          <span className="mp-metric__note">Average gap between predicted and surveyed prevalence, in percentage points</span>
        </div>
        <div className="mp-metric">
          <span className="mp-metric__value">{lowo.rmse_pct_points.toFixed(1)} pts</span>
          <span className="mp-metric__label">Root mean squared error (RMSE) · LOWO</span>
          <span className="mp-metric__note">
            Like mean absolute error, but big misses count much more than small ones, so a value well above the mean
            absolute error means a few sites are missed by a lot
          </span>
        </div>
        <div className="mp-metric">
          <span className="mp-metric__value">{lowo.same_level_pct.toFixed(1)}%</span>
          <span className="mp-metric__label">Same risk level · LOWO</span>
          <span className="mp-metric__note">Predicted and surveyed in the same level: Low &lt;10%, Moderate 10–49.9%, High ≥50%</span>
        </div>
        <div className="mp-metric">
          <span className="mp-metric__value">{accuracyValue(perf).toFixed(1)}%</span>
          <span className="mp-metric__label">Accuracy · LOWO · by MAE</span>
          <span className="mp-metric__note">{ACCURACY_NOTE}</span>
        </div>
      </div>
      <p className="mp-method">
        {lowo.locations} training data sites in {lowo.woredas} woredas. LOWO (Leave-One-Woreda-Out): all sites of each
        woreda are predicted by a model trained on the other woredas only, a woreda the model has never seen. Accuracy:
        each site is predicted by a model that did not train on that site
        {perf.reviews_used ? ` (plus ${perf.reviews_used} reviewed records in training)` : ""}.
        {perf.measured_at && ` Measured ${new Date(perf.measured_at).toLocaleString()}.`} These are retrospective
        numbers on the training survey area; they are not proof the model works elsewhere.
      </p>
      {outdated && (
        <p className="mp-warn">
          <FiAlertTriangle /> Measured on an earlier version of the model; the numbers may not match the current one.
        </p>
      )}
    </div>
  );
}
